import React, { useState, useEffect } from 'react'
import { X, Upload, Image as ImageIcon, Link, Eye, EyeOff } from 'lucide-react'

const CarouselEditor = ({ carousel, onSave, onCancel, isEditing }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    video_url: '',
    media_type: 'image',
    target_url: '',
    display_order: 0,
    is_active: true,
    auto_play_duration: 5000
  })

  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [previewImage, setPreviewImage] = useState('')

  // 如果是编辑模式，初始化表单数据
  useEffect(() => {
    if (carousel) {
      setFormData({
        title: carousel.title || '',
        description: carousel.description || '',
        image_url: carousel.image_url || '',
        video_url: carousel.video_url || '',
        media_type: carousel.media_type || 'image',
        target_url: carousel.target_url || '',
        display_order: carousel.display_order || 0,
        is_active: carousel.is_active !== undefined ? carousel.is_active : true,
        auto_play_duration: carousel.auto_play_duration || 5000
      })
      if (carousel.image_url) {
        setPreviewImage(carousel.image_url)
      }
    }
  }, [carousel])

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleNumberChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value === '' ? '' : Number(value)
    }))
  }

  const handleFileUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    // 验证文件类型
    if (!file.type.startsWith('image/')) {
      alert('请选择图片文件')
      return
    }

    // 验证文件大小（限制为10MB）
    if (file.size > 10 * 1024 * 1024) {
      alert('图片大小不能超过10MB')
      return
    }

    setUploading(true)
    setUploadProgress(0)

    const formData = new FormData()
    formData.append('image', file)

    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/api/carousel/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      })

      // 模拟上传进度
      const interval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(interval)
            return 90
          }
          return prev + 10
        })
      }, 100)

      const data = await response.json()

      clearInterval(interval)
      setUploadProgress(100)

      if (data.success) {
        const imageUrl = data.data.imageUrl
        setFormData(prev => ({ ...prev, image_url: imageUrl }))
        setPreviewImage(imageUrl)
        setTimeout(() => {
          setUploading(false)
          setUploadProgress(0)
        }, 500)
      } else {
        throw new Error(data.message || '上传失败')
      }
    } catch (error) {
      console.error('上传失败:', error)
      alert(`上传失败: ${error.message}`)
      setUploading(false)
      setUploadProgress(0)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    
    // 验证必填字段
    if (!formData.title.trim()) {
      alert('请输入标题')
      return
    }

    if (!formData.image_url.trim()) {
      alert('请上传图片')
      return
    }

    // 转换数字字段
    const submitData = {
      ...formData,
      display_order: Number(formData.display_order) || 0,
      auto_play_duration: Number(formData.auto_play_duration) || 5000
    }

    onSave(submitData)
  }

  const handleRemoveImage = () => {
    setFormData(prev => ({ ...prev, image_url: '' }))
    setPreviewImage('')
  }

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              {isEditing ? '编辑轮播图' : '添加轮播图'}
            </h3>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-500"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* 图片上传区域 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              轮播图图片 *
            </label>
            <div className="space-y-4">
              {previewImage ? (
                <div className="relative">
                  <img
                    src={previewImage}
                    alt="预览"
                    className="w-full h-48 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors">
                  <ImageIcon className="mx-auto text-gray-400" size={48} />
                  <p className="mt-2 text-sm text-gray-600">
                    点击或拖拽图片到此处上传
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    支持 JPG, PNG, GIF 格式，建议尺寸 1920×600
                  </p>
                  <input
                    type="file"
                    id="image-upload"
                    className="hidden"
                    accept="image/*"
                    onChange={handleFileUpload}
                    disabled={uploading}
                  />
                  <label
                    htmlFor="image-upload"
                    className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer"
                  >
                    <Upload className="mr-2" size={16} />
                    {uploading ? '上传中...' : '选择图片'}
                  </label>
                </div>
              )}

              {uploading && (
                <div className="mt-4">
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>上传进度</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              {formData.image_url && !previewImage && (
                <div className="text-sm text-gray-500">
                  已设置图片URL: {formData.image_url.substring(0, 50)}...
                </div>
              )}
            </div>
          </div>

          {/* 基本信息 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                标题 *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="请输入轮播图标题"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                显示顺序
              </label>
              <input
                type="number"
                name="display_order"
                value={formData.display_order}
                onChange={handleNumberChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="0"
                min="0"
              />
              <p className="mt-1 text-xs text-gray-500">数字越小，显示越靠前</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              描述
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="请输入轮播图描述"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              跳转链接
            </label>
            <div className="flex items-center space-x-2">
              <Link size={16} className="text-gray-400" />
              <input
                type="url"
                name="target_url"
                value={formData.target_url}
                onChange={handleInputChange}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="https://example.com"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">点击轮播图时跳转的链接（可选）</p>
          </div>

          {/* 高级设置 */}
          <div className="border-t border-gray-200 pt-6">
            <h4 className="text-sm font-medium text-gray-900 mb-4">高级设置</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  自动播放时长（毫秒）
                </label>
                <input
                  type="number"
                  name="auto_play_duration"
                  value={formData.auto_play_duration}
                  onChange={handleNumberChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="5000"
                  min="1000"
                  step="1000"
                />
                <p className="mt-1 text-xs text-gray-500">设置为0则不自动播放</p>
              </div>

              <div className="flex items-center space-x-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                    启用轮播图
                  </label>
                </div>
                <div className="flex items-center">
                  {formData.is_active ? (
                    <Eye className="text-green-500" size={16} />
                  ) : (
                    <EyeOff className="text-gray-400" size={16} />
                  )}
                  <span className="ml-1 text-sm text-gray-600">
                    {formData.is_active ? '已启用' : '已禁用'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* 操作按钮 */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              取消
            </button>
            <button
              type="submit"
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {isEditing ? '更新轮播图' : '创建轮播图'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CarouselEditor
